<template>
  <div v-if="data && isDisplayed && isRawValueValid">
    <!-- #1689: the whole value is always in the HTML (search engines and AI
         fetchers read the server render). Long values are clamped visually with a
         CSS line clamp, never cut or hidden, and the toggle below expands them. -->
    <div
      ref="clampEl"
      :class="{ 'datum-clamp': isValueTruncated }"
      :style="isValueTruncated ? { '--datum-clamp-lines': clampLines } : null"
    >
    <span class="font-weight-bold">
      <template v-if="isValueAnArray">
          {{ filters.pluralize(filters.capitalize(filterConfig.displayName), pluralizeCount) }}:
      </template>
      <template v-else>
        {{ filters.capitalize(filterConfig.displayName) }}:
      </template>
    </span>

    <span v-if="valueEntityLinks">
      <!--
        A mixed list is normal: a byline entry that never resolved to a profile
        (e.g. a group author like "WHO Consultation") comes through with no id.
        Render it as text rather than a link that goes nowhere — and crucially,
        don't let it stop its neighbours from linking (oxjob #619).
      -->
      <template v-for="(entityObj, i) in valueEntityLinks" :key="(entityObj?.id || 'unlinked-' + i) + i">
        <router-link
          v-if="entityObj?.id"
          :to="filters.entityZoomLink(entityObj.id)"
          class="mr-1 pr-0"
        >
          {{ entityObj?.raw_author_name || entityObj?.display_name || 'Unknown' }}{{ scorePercent(entityObj) }}{{ i + 1 < valueEntityLinks.length ? ", " : "" }}
        </router-link>
        <span v-else class="mr-1 pr-0">
          {{ entityObj?.raw_author_name || entityObj?.display_name || 'Unknown' }}{{ i + 1 < valueEntityLinks.length ? ", " : "" }}
        </span>
      </template>
    </span>

    <span v-if="isObservedNamesOwnerRow">
      <template v-for="(item, i) in ownerVisibleNames" :key="item.name + i">
        <v-tooltip v-if="item.pending" location="top" text="Deletion pending">
          <template #activator="{ props: tipProps }">
            <span
              v-bind="tipProps"
              class="mr-1 pr-0 text-decoration-line-through text-medium-emphasis"
            >{{ item.name }}<v-icon size="x-small" class="ml-1">mdi-timer-sand</v-icon>{{ i + 1 < ownerVisibleNames.length ? "," : "" }}</span>
          </template>
        </v-tooltip>
        <span
          v-else
          class="mr-1 pr-0"
        >{{ item.name }}{{ i + 1 < ownerVisibleNames.length ? "," : "" }}</span>
      </template>
      <v-btn
        variant="text"
        size="x-small"
        class="ml-1 observed-names-edit-btn"
        icon
        aria-label="Edit observed names"
        @click="ownerCuration.openDialog()"
      >
        <v-icon size="16">mdi-pencil-outline</v-icon>
      </v-btn>
    </span>

    <span v-else-if="valueListOfStrings">
      <span
        v-for="(str, i) in valueListOfStrings"
        :key="str + i"
        class="mr-1 pr-0"
      >
        {{ str }}{{ i + 1 < valueListOfStrings.length ? ", " : "" }}
      </span>
    </span>

    <span v-else-if="valueId">
      <span>{{ valueId }}</span>
    </span>

    <span v-else-if="valueUrl">
      <a :href="rawValue" target="_blank">
        {{ valueUrl }}
        <v-icon size="x-small" class="ml-1">mdi-open-in-new</v-icon>
      </a>
    </span>

    <span v-else-if="valueString">
      <span>{{ valueString }}</span>
    </span>

    <span v-else-if="valueWorksCount !== null">
      <router-link v-if="valueWorksCount > 0 && valueWorksCountLink" :to="valueWorksCountLink">
        {{ filters.toPrecision(valueWorksCount) }}
      </router-link>
      <span v-else>{{ valueWorksCount > 0 ? filters.toPrecision(valueWorksCount) : 0 }}</span>
    </span>

    <span v-else-if="valueUnlinkedCount">
      <span>{{ isValueUsd ? "$" : awardCurrencySymbol }}{{ filters.toPrecision(valueUnlinkedCount) }}</span>
    </span>

    <span v-else-if="valueLinkedCount">
      <router-link :to="url.makeFilterRoute(entityType, filterKeyForMakingLinks, props.data.id)">
        {{ filters.toPrecision(valueLinkedCount) }}
      </router-link>
    </span>

    <span v-else-if="valueBoolean">
      {{ valueBoolean }}
    </span>

    <!-- "none" is the second v-if chain's fallback, so it must also check the
         FIRST chain (valueEntityLinks renders in its own v-if above) — else a
         locations row that links an entity gets a stray "none" appended
         (oxjob #852: "Work: W100000002 none"). -->
    <span v-else-if="showMissing && !valueEntityLinks" class="text-grey">
      none
    </span>
    </div>

    <button
      v-if="showClampToggle"
      type="button"
      class="datum-clamp-toggle font-weight-bold text-primary"
      :aria-expanded="isValueTruncated ? 'false' : 'true'"
      @click="isTruncateSet = !isTruncateSet"
    >
      <template v-if="!isValueTruncated">(less)</template>
      <template v-else-if="isValueAnArray">show all {{ filters.toPrecision(valueLength) }}</template>
      <template v-else>more</template>
    </button>

  </div>
</template>

<script setup>
import { ref, computed, inject, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

import ISO6391 from 'iso-639-1';

import filters from '@/filters';
import { url } from '@/url';
import { getFacetConfig } from '@/facetConfigUtils';
import * as openalexId from '@/openalexId';
import * as entityDatumValues from '@/components/Entity/entityDatumValues';

defineOptions({ name: 'EntityDatumRow' });

const props = defineProps({
  filterKey: String,
  type: String,
  data: Object,
  showMissing: {
    type: Boolean,
    default: false
  }
});

// Get entity type from props.type or fallback to guessing from ID
const entityType = computed(() => props.type || openalexId.getEntityType(props.data?.id));

// oxjob #342 — owner-only "remove observed names". EntityPage provides this
// context so the deeply-nested observed-names row can render a pencil + strike
// pending / hide removed names without prop-drilling through EntityNew. Null
// for non-owners and every other row, which keeps the generic rendering path.
const ownerCuration = inject('observedNamesOwnerCuration', null);
const isObservedNamesOwnerRow = computed(
  () =>
    !!ownerCuration?.enabled?.value &&
    entityType.value === 'authors' &&
    props.filterKey === 'display_name_alternatives'
);
// Names to render for the owner: hide fully-removed names (curation-derived,
// EXPLORE Q2 — the server doc lags >24h), flag still-pending ones as struck.
const ownerVisibleNames = computed(() => {
  if (!isObservedNamesOwnerRow.value || !Array.isArray(rawValue.value)) return [];
  return rawValue.value
    .filter((name) => !ownerCuration.isNameRemoved(name))
    .map((name) => ({ name, pending: ownerCuration.isNamePending(name) }));
});

const isTruncateSet = ref(true);
const maxLen = ref({ string: 200, array: 5 });

const filterConfig = computed(() => {
  const config = getFacetConfig(props.type, props.filterKey);
  // For repositories, use a clearer collection than "Fully open access"
  if (props.filterKey === 'is_oa' && props.data?.type === 'repository') {
    return { ...config, displayName: 'Full text for all works' };
  }
  // A facet can carry an entity-page-only label (e.g. authors' "observed institutions") without
  // renaming the filter UI, which shares the same displayName.
  if (config?.displayNameOnEntityPage) {
    return { ...config, displayName: config.displayNameOnEntityPage };
  }
  return config;
});
const rawValue = computed(() => {
  if (!filterConfig.value || !props.data) return null;
  try {
    return filterConfig.value.extractFn(props.data);
  } catch (e) {
    console.error('Error extracting value:', e, 'filterKey:', props.filterKey, 'entityType:', props.type);
    return null;
  }
});
const myValueType = computed(() => Array.isArray(rawValue.value) ? 'array' : typeof rawValue.value);
const isValueAnArray = computed(() => Array.isArray(rawValue.value));
// oxjob #1307: facets with showScorePercent (a keyword's related topics) show each link's score as a share, " (70%)"
const scorePercent = (entityObj) => (filterConfig.value?.showScorePercent && typeof entityObj?.score === "number")
  ? ` (${Math.round(entityObj.score * 100)}%)` : "";
const valueLength = computed(() => rawValue.value?.length);

const isRawValueValid = computed(() => {
  if (myValueType.value === 'array' && !rawValue.value.every(o => o !== undefined && o !== null)) {
    return false;
  }
  return true;
});

const isValueSubjectToTruncation = computed(() => {
  const type = myValueType.value;
  return ['string', 'array'].includes(type) && rawValue.value.length > maxLen.value[type];
});
const isValueTruncated = computed(() => isValueSubjectToTruncation.value && isTruncateSet.value);

// #1689: clamp height in lines (abstract: 3; lists such as authors: 2). The server
// can't measure, so it shows the toggle by length alone; in the browser the toggle
// goes away when the clamped value turns out to fit.
const clampLines = computed(() => (isValueAnArray.value ? 2 : 3));
const clampEl = ref(null);
const clampOverflows = ref(null); // null until measured in the browser
function measureClamp() {
  const el = clampEl.value;
  if (!el || !isValueTruncated.value) return;
  clampOverflows.value = el.scrollHeight > el.clientHeight + 1;
}
const showClampToggle = computed(() =>
  isValueSubjectToTruncation.value && (!isValueTruncated.value || clampOverflows.value !== false)
);
let clampObserver = null;
onMounted(() => {
  measureClamp();
  if (typeof ResizeObserver !== 'undefined' && clampEl.value) {
    clampObserver = new ResizeObserver(() => measureClamp());
    clampObserver.observe(clampEl.value);
  }
});
onBeforeUnmount(() => clampObserver?.disconnect());
watch([rawValue, isValueTruncated], () => nextTick(measureClamp));

const isDisplayed = computed(() => {
  // If showMissing is true, always display the row
  if (props.showMissing) {
    return true;
  }
  if (isValueAnArray.value) return !!rawValue.value.length;
  // An institution's wider works counts show only when they add works (#1823).
  const narrower = INSTITUTION_NARROWER_COUNT[props.filterKey];
  if (narrower && entityType.value === 'institutions') {
    return typeof rawValue.value === 'number' && rawValue.value > (props.data?.[narrower] ?? 0);
  }
  return rawValue.value !== null && rawValue.value !== undefined;
});

const INSTITUTION_NARROWER_COUNT = {
  works_count_with_children: 'works_count',
  works_count_with_children_and_predecessors: 'works_count_with_children',
};

const valueEntityLinks = computed(() => {
  return entityDatumValues.entityLinkItems(rawValue.value);
});

const valueListOfStrings = computed(() => {
  // isId arrays (e.g. authors' observed_orcids) carry full ID URLs; strip them
  // to bare ids the same way the single-value `valueId` computed below does.
  const items = entityDatumValues.stringItems(rawValue.value, {
    stripIdPrefix: !!filterConfig.value?.isId,
  });
  return items;
});

const valueString = computed(() => {
  if (props.filterKey === 'publication_year') return rawValue.value;
  if (typeof rawValue.value === 'string') {
    let displayValue = rawValue.value;
    
    // Convert language ISO code to full name
    if (props.filterKey === 'language') {
      const languageName = ISO6391.getName(rawValue.value);
      displayValue = languageName || rawValue.value; // Fallback to code if not found
    }
    
    return displayValue;
  }
  return null;
});

const valueWorksCount = computed(() => {
  if (['works_count', 'funded_outputs_count', 'awards_count',
    'works_count_with_children', 'works_count_with_children_and_predecessors'].includes(props.filterKey)) {
    return rawValue.value ?? null;  // Return the value (including 0), or null if undefined
  }
  return null;
});

const valueWorksCountLink = computed(() => {
  if (props.filterKey === 'awards_count') {
    return filters.funderAwardsLink(props.data?.id);
  }
  // Each institution count opens the works it counts: works_count the works
  // linked to the institution itself, the widest count the lineage filter. No
  // filter selects "with children but not predecessors", so that count has no link (#1823).
  if (props.filterKey === 'works_count' && entityType.value === 'institutions') {
    return filters.entityWorksLink(props.data?.id, props.data, 'authorships.institutions.id');
  }
  if (props.filterKey === 'works_count_with_children_and_predecessors') {
    return filters.entityWorksLink(props.data?.id, props.data);
  }
  if (props.filterKey === 'works_count' || props.filterKey === 'funded_outputs_count') {
    return filters.entityWorksLink(props.data?.id, props.data);
  }
  return null;
});
const valueUnlinkedCount = computed(() => (typeof rawValue.value === 'number' && filterConfig.value.type !== 'selectEntity' ? rawValue.value : null));
const valueLinkedCount = computed(() => (typeof rawValue.value === 'number' && filterConfig.value.type === 'selectEntity' ? rawValue.value : null));
const isValueUsd = computed(() => props.filterKey === 'apc_paid.value_usd');
const isAwardAmount = computed(() => props.filterKey === 'amount' && entityType.value === 'awards');
const awardCurrencySymbol = computed(() => {
  if (!isAwardAmount.value) return '';
  const currency = props.data?.currency;
  const symbols = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$', JPY: '¥', CNY: '¥' };
  return symbols[currency] || (currency ? `${currency} ` : '$');
});
// PIDs (ORCID, ROR, ISSN, DOI, OpenAlex ID, ...) render as the bare identifier:
// no hyperlink, protocol + domain stripped (e.g. "0000-0002-1825-0097").
const valueId = computed(() => {
  if (!filterConfig.value?.isId) return null;
  const raw = rawValue.value;
  if (typeof raw !== 'string' || !raw.length) return null;
  return raw.replace(/^https?:\/\/[^/]+\//, '');
});

// Show URL values as clickable domain names (e.g., "elsevier.com")
const valueUrl = computed(() => {
  if (typeof rawValue.value !== 'string' || !rawValue.value.startsWith('http')) return null;
  try {
    const hostname = new URL(rawValue.value).hostname.replace(/^www\./, '');
    return hostname;
  } catch {
    return rawValue.value.replace(/^https?:\/\//, '').split('/')[0];
  }
});

const valueBoolean = computed(() => {
  if (typeof rawValue.value === 'boolean') {
    return rawValue.value ? 'Yes' : 'No';
  }
  // If showMissing is true and the field type is boolean, show Yes/No based on truthiness
  if (props.showMissing && filterConfig.value?.type === 'boolean') {
    return rawValue.value ? 'Yes' : 'No';
  }
  return null;
});

const pluralizeCount = computed(() => {
  if (props.filterKey === 'cites') return 2;
  if (filterConfig.value.displayName.endsWith(')')) return 1;
  return isValueAnArray.value && rawValue.value?.length > 1 ? 2 : 1;
});

const filterKeyForMakingLinks = computed(() => {
  if (props.filterKey === 'cites') return 'cited_by';
  else if (props.filterKey === 'cited_by') return 'cites';
  else return props.filterKey;
});
</script>


<style scoped lang="scss">
a {
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.datum-clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--datum-clamp-lines);
  line-clamp: var(--datum-clamp-lines);
  overflow: hidden;
}

.datum-clamp-toggle {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.observed-names-edit-btn {
  opacity: 0.4;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
}

</style>